import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { APP_CURRENCY, APP_LOCALE } from "../../../config/locale";
import {
  ApiError,
  categorizeWorkFinancialEntry,
  createSupplierFromWorkFinancialEntry,
  deleteWorkFinancialDocument,
  getExpenseCategories,
  getFinancialApprovalStatuses,
  getWorkFinancialDocumentFileUrl,
  getWorkFinancialDocuments,
  imputeWorkFinancialEntryCost,
  matchWorkFinancialEntrySupplier,
  removeWorkFinancialEntryCostImputation,
  reprocessWorkFinancialDocument,
  setWorkFinancialEntryApprovalStatus,
  unvalidateWorkFinancialEntry,
  updateWorkFinancialEntry,
  uploadWorkFinancialDocument,
  validateWorkFinancialEntry,
  type ExpenseCategory,
  type FinancialAnomaly,
  type FinancialApprovalStatus,
  type UpdateWorkFinancialEntryPayload,
  type WorkFinancialDocument,
  type WorkFinancialDocumentKind,
  type WorkFinancialEntry,
  type WorkFinancialEntryType,
} from "../../../obras/obrasApi";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "../../../components/SearchableSelect";
import { useLookupOptions } from "../../../lookups/useLookupOptions";
import { EntityDocumentsSection } from "../../../components/EntityDocumentsSection";
import { Modal } from "../../../components/Modal";
import { DetailsToggle } from "../../../components/DetailsToggle";

interface WorkFinancialDocumentsTabProps {
  workId: number;
  // Bumped by the parent so the "Custos" tab + ficha cards re-fetch the new SpentValue.
  onCostImputed?: () => void;
}

const formatCurrency = (value?: number | null): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat(APP_LOCALE, {
    style: "currency",
    currency: APP_CURRENCY,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value).replace(/\./g, " ");
};

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

const statusLabel = (document: WorkFinancialDocument): string => {
  if (document.processingStatus === "Pending") return "Em fila";
  if (document.processingStatus === "Processing") return "A processar";
  if (document.processingStatus === "Failed") return "Falhou";
  if (document.entry?.validationStatus === "Validated") return "Validado";
  return "Por validar";
};

const statusClass = (document: WorkFinancialDocument): string => {
  if (document.processingStatus === "Failed") return "bg-destructive/10 text-destructive border-destructive/30";
  if (document.processingStatus === "Pending" || document.processingStatus === "Processing") return "bg-amber-500/10 text-amber-700 border-amber-500/30";
  if (document.entry?.validationStatus === "Validated") return "bg-emerald-500/10 text-emerald-700 border-emerald-500/30";
  return "bg-primary/10 text-primary border-primary/30";
};

const toInputDate = (value?: string | null): string => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const parseNumber = (value: string): number | null => {
  const normalized = value.trim().replace(/\s/g, "").replace(",", ".");
  if (!normalized) return null;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export function WorkFinancialDocumentsTab({ workId, onCostImputed }: WorkFinancialDocumentsTabProps) {
  const [documents, setDocuments] = useState<WorkFinancialDocument[]>([]);
  const [kindHint, setKindHint] = useState<WorkFinancialDocumentKind | "">("");
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingEntry, setEditingEntry] = useState<WorkFinancialEntry | null>(null);
  const [draft, setDraft] = useState<UpdateWorkFinancialEntryPayload | null>(null);
  const [expenseCategories, setExpenseCategories] = useState<ExpenseCategory[]>([]);
  const suppliersLookup = useLookupOptions("suppliers");
  const [approvalStatuses, setApprovalStatuses] = useState<FinancialApprovalStatus[]>([]);
  const [busyEntryId, setBusyEntryId] = useState<number | null>(null);
  const [expandedEntryId, setExpandedEntryId] = useState<number | null>(null);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  // [2.1.12] Resumo do último lote de upload (ex. "3 de 4 enviadas; 1 falhou").
  const [batchSummary, setBatchSummary] = useState<string | null>(null);
  const toggleExpanded = (id: number) => setExpandedEntryId((prev) => (prev === id ? null : id));

  const handleDelete = async (documentId: number) => {
    if (deletingId !== null) return;
    if (!window.confirm("Eliminar este documento financeiro?")) return;
    setDeletingId(documentId);
    setError(null);
    try {
      await deleteWorkFinancialDocument(workId, documentId);
      setDocuments((prev) => prev.filter((item) => item.id !== documentId));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setDocuments((prev) => prev.filter((item) => item.id !== documentId));
      } else {
        setError(err instanceof Error ? err.message : "Não foi possível eliminar o documento.");
      }
      loadDocuments(true);
    } finally {
      setDeletingId(null);
    }
  };

  const loadDocuments = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      setDocuments(await getWorkFinancialDocuments(workId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar os documentos financeiros.");
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [workId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    const hasProcessing = documents.some((document) =>
      document.processingStatus === "Pending" || document.processingStatus === "Processing",
    );
    if (!hasProcessing) return;
    const handle = window.setInterval(() => loadDocuments(true), 4000);
    return () => window.clearInterval(handle);
  }, [documents, loadDocuments]);

  useEffect(() => {
    let active = true;
    Promise.all([getExpenseCategories(), getFinancialApprovalStatuses()])
      .then(([categories, statuses]) => {
        if (!active) return;
        setExpenseCategories(categories);
        setApprovalStatuses(statuses);
      })
      .catch(() => {
        // Non-fatal: intelligence controls degrade gracefully if reference data is unavailable.
      });
    return () => {
      active = false;
    };
  }, []);

  const runEntryMutation = useCallback(
    async (entryId: number, mutate: () => Promise<unknown>) => {
      if (busyEntryId !== null) return;
      setBusyEntryId(entryId);
      setError(null);
      try {
        await mutate();
        // Re-fetch so server-computed match state / suggestions update immediately.
        await loadDocuments(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Não foi possível atualizar o registo.");
      } finally {
        setBusyEntryId(null);
      }
    },
    [busyEntryId, loadDocuments],
  );

  const handleCategorize = (entryId: number, expenseCategoryId: number | null) =>
    runEntryMutation(entryId, () => categorizeWorkFinancialEntry(workId, entryId, expenseCategoryId));

  const handleMatchSupplier = (entryId: number, supplierId: number) =>
    runEntryMutation(entryId, () => matchWorkFinancialEntrySupplier(workId, entryId, supplierId));

  const handleCreateSupplier = (entryId: number) =>
    runEntryMutation(entryId, async () => {
      // `createSupplierFromWorkFinancialEntry` já invalida a lista partilhada de
      // fornecedores, e a invalidação avisa este hook — pedir `reload()` aqui só
      // duplicava o pedido.
      await createSupplierFromWorkFinancialEntry(workId, entryId);
    });

  // Impute/anular share the busyEntryId guard + local refetch with the other mutations,
  // and additionally bump the obra's cost refreshKey so the Custos tab + ficha cards update.
  const handleImputeCost = (entryId: number) =>
    runEntryMutation(entryId, async () => {
      await imputeWorkFinancialEntryCost(workId, entryId);
      onCostImputed?.();
    });

  const handleRemoveImputation = (entryId: number) =>
    runEntryMutation(entryId, async () => {
      await removeWorkFinancialEntryCostImputation(workId, entryId);
      onCostImputed?.();
    });

  // [2.1.7] Persiste o estado de aprovação imediatamente (recebida/aprovada/paga/contestada),
  // tal como os outros selects inline (categoria, fornecedor).
  const handleApprovalStatusChange = (entryId: number, statusCode: string) =>
    runEntryMutation(entryId, () => setWorkFinancialEntryApprovalStatus(workId, entryId, statusCode));

  const categoryIdByCode = useCallback(
    (code?: string | null): number | null => {
      if (!code) return null;
      return expenseCategories.find((category) => category.code === code)?.id ?? null;
    },
    [expenseCategories],
  );

  const entries = useMemo(
    () => documents.map((document) => document.entry).filter(Boolean) as WorkFinancialEntry[],
    [documents],
  );
  const revenues = entries.filter((entry) => entry.entryType === "Revenue");
  const expenses = entries.filter((entry) => entry.entryType === "Expense");

  // [2.1.12] Multi-upload: fan-out no cliente. O kindHint selecionado aplica-se a TODO o lote.
  // O pipeline de OCR já lida com N documentos; cada um fica Pending→processa e o polling existente atualiza-os.
  const handleUpload = async (files: FileList | null) => {
    const list = files ? Array.from(files) : [];
    if (list.length === 0) return;
    if (!kindHint) return;
    setIsUploading(true);
    setError(null);
    setBatchSummary(null);
    try {
      const results = await Promise.allSettled(
        list.map((file) => uploadWorkFinancialDocument(workId, file, kindHint)),
      );
      const created = results
        .filter((r): r is PromiseFulfilledResult<WorkFinancialDocument> => r.status === "fulfilled")
        .map((r) => r.value);
      const failed = results.length - created.length;
      if (created.length > 0) {
        setDocuments((prev) => [...created, ...prev]);
      }
      if (failed > 0) {
        setBatchSummary(
          created.length > 0
            ? `${created.length} de ${results.length} enviada(s); ${failed} falhou/falharam.`
            : `Falha ao carregar ${failed} documento(s).`,
        );
        // Mantém o modal aberto se nada passou, para o utilizador tentar de novo.
        if (created.length > 0) setIsUploadOpen(false);
      } else {
        setBatchSummary(`${created.length} documento(s) enviado(s).`);
        setIsUploadOpen(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar o documento.");
    } finally {
      setIsUploading(false);
    }
  };

  const openEdit = (entry: WorkFinancialEntry) => {
    setEditingEntry(entry);
    setDraft({
      entryType: entry.entryType,
      documentNumber: entry.documentNumber ?? "",
      documentDate: toInputDate(entry.documentDate),
      entityName: entry.entityName ?? "",
      entityTaxNumber: entry.entityTaxNumber ?? "",
      description: entry.description ?? "",
      baseAmount: entry.baseAmount ?? null,
      vatAmount: entry.vatAmount ?? null,
      totalAmount: entry.totalAmount ?? null,
      currencyCode: entry.currencyCode ?? "EUR",
    });
  };

  const saveEdit = async () => {
    if (!editingEntry || !draft) return;
    const updated = await updateWorkFinancialEntry(workId, editingEntry.id, draft);
    setDocuments((prev) => prev.map((document) =>
      document.entry?.id === updated.id ? { ...document, entry: updated } : document,
    ));
    setEditingEntry(null);
    setDraft(null);
  };

  const replaceDocument = (updated: WorkFinancialDocument) => {
    setDocuments((prev) => prev.map((document) => document.id === updated.id ? updated : document));
  };

  const actionPillClass =
    "inline-flex items-center px-3 py-2 text-sm font-medium text-primary border border-primary/20 disabled:opacity-50 disabled:cursor-not-allowed";
  const actionPillStyle = { borderRadius: "var(--radius-button)" } as const;
  const categoryOptions = useMemo<SearchableSelectOption<number>[]>(
    () =>
      expenseCategories.map((category) => ({
        value: category.id,
        label: category.name,
      })),
    [expenseCategories],
  );


  const renderSupplierChip = (entry: WorkFinancialEntry) => {
    const busy = busyEntryId === entry.id;
    const manualSelect = (label: string) => (
      <SearchableSelect<number>
        value={null}
        disabled={busy}
        onChange={(next) => {
          if (next != null) handleMatchSupplier(entry.id, next);
        }}
        options={suppliersLookup.options}
        loading={suppliersLookup.loading}
        placeholder={label}
        searchPlaceholder="Procurar fornecedor…"
        ariaLabel={label}
        panelPosition="fixed"
        style={{ minWidth: 220, maxWidth: "100%" }}
      />
    );

    if (entry.supplierMatchState === "Linked") {
      return (
        <span
          className="inline-flex items-center px-2 py-1 text-xs font-medium bg-primary/10 text-primary border border-primary/20"
          style={{ borderRadius: "var(--radius)" }}
          title="Fornecedor associado"
        >
          {entry.matchedSupplierName || "Fornecedor associado"}
        </span>
      );
    }
    if (entry.supplierMatchState === "Suggested") {
      return (
        <div className="flex flex-col gap-2">
          <span className="text-sm text-secondary-text">
            Sugerido: <span className="text-foreground font-medium">{entry.suggestedSupplierName || "—"}</span>
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={actionPillClass}
              style={actionPillStyle}
              disabled={busy || entry.suggestedSupplierId == null}
              onClick={() => entry.suggestedSupplierId != null && handleMatchSupplier(entry.id, entry.suggestedSupplierId)}
            >
              {busy ? "A associar…" : "Associar"}
            </button>
            {manualSelect("Outro fornecedor…")}
          </div>
        </div>
      );
    }
    if (entry.supplierMatchState === "NotFound") {
      return (
        <div className="flex flex-col gap-2">
          <span className="text-sm text-secondary-text">Sem correspondência por NIF</span>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={actionPillClass}
              style={actionPillStyle}
              disabled={busy}
              onClick={() => handleCreateSupplier(entry.id)}
            >
              {busy ? "A criar…" : "+ Criar fornecedor"}
            </button>
            {manualSelect("Associar existente…")}
          </div>
        </div>
      );
    }
    return <span className="text-secondary-text text-xs" title="Sem NIF extraído">— (sem NIF)</span>;
  };

  const renderCategoryCell = (entry: WorkFinancialEntry) => {
    const busy = busyEntryId === entry.id;
    const confirmed = entry.expenseCategoryId != null;
    const suggestedId = !confirmed ? categoryIdByCode(entry.suggestedExpenseCategoryCode) : null;
    return (
      <div className="flex flex-col gap-2">
        <SearchableSelect<number>
          ariaLabel="Categoria de despesa"
          value={entry.expenseCategoryId ?? null}
          disabled={busy}
          onChange={(next) => handleCategorize(entry.id, next)}
          options={categoryOptions}
          placeholder="— Sem categoria —"
          searchPlaceholder="Procurar categoria…"
          panelPosition="fixed"
          style={{ minWidth: 220, maxWidth: "100%" }}
        />
        {!confirmed && suggestedId != null && (
          <button
            type="button"
            className={actionPillClass}
            style={{ ...actionPillStyle, alignSelf: "flex-start" }}
            disabled={busy}
            onClick={() => handleCategorize(entry.id, suggestedId)}
          >
            {busy ? "A aceitar…" : `Usar sugestão: ${entry.suggestedExpenseCategoryName || "—"}`}
          </button>
        )}
      </div>
    );
  };

  // [A-19] Coluna de imputação para despesas: só Despesas validadas podem virar custo.
  // Mirrors the lote-16 "Imputar custo" / "Imputado · {valor} · Anular" UX from WorkOrdersTab.
  const renderImputeCell = (entry: WorkFinancialEntry) => {
    if (entry.entryType !== "Expense") return null;
    const busy = busyEntryId === entry.id;
    if (entry.validationStatus !== "Validated") {
      return <span className="text-secondary-text text-sm">Valide a fatura para a imputar como custo.</span>;
    }
    if (entry.imputedCostId != null) {
      return (
        <div className="flex flex-col gap-2" style={{ alignItems: "flex-start" }}>
          <span
            className="inline-flex items-center px-3 py-2 text-sm font-medium bg-primary/10 text-primary border border-primary/20"
            style={{ borderRadius: "var(--radius)" }}
          >
            Imputado · {formatCurrency(entry.imputedAmount ?? 0)}
          </span>
          <button
            type="button"
            onClick={() => handleRemoveImputation(entry.id)}
            disabled={busy}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-destructive border border-border disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            {busy ? "A anular…" : "Anular"}
          </button>
        </div>
      );
    }
    return (
      <button
        type="button"
        onClick={() => handleImputeCost(entry.id)}
        disabled={busy}
        className={`${actionPillClass} whitespace-nowrap`}
        style={actionPillStyle}
      >
        {busy ? "A imputar…" : "Imputar custo"}
      </button>
    );
  };

  // [2.1.7] Select inline do estado de aprovação — persiste de imediato (mesmo padrão dos
  // outros selects da linha). Mostra o nome do estado atual.
  const renderApprovalSelect = (entry: WorkFinancialEntry) => {
    const busy = busyEntryId === entry.id;
    return (
      <select
        value={entry.approvalStatusCode || ""}
        disabled={busy || approvalStatuses.length === 0}
        onChange={(e) => {
          const code = e.target.value;
          if (code && code !== entry.approvalStatusCode) handleApprovalStatusChange(entry.id, code);
        }}
        className="border border-border bg-background text-foreground text-sm"
        style={{ borderRadius: "var(--radius-button)", padding: "4px 8px", maxWidth: "100%" }}
        title="Estado de aprovação do documento"
        aria-label="Estado de aprovação"
      >
        {/* "sem-estado" é o sentinela do backend para NULL — escolher limpa o estado. */}
        <option value="sem-estado">Sem estado</option>
        {/* Mostra o estado atual mesmo que a lista ainda não tenha carregado. */}
        {approvalStatuses.length === 0 && entry.approvalStatusCode && entry.approvalStatusCode !== "sem-estado" && (
          <option value={entry.approvalStatusCode}>{entry.approvalStatusName || entry.approvalStatusCode}</option>
        )}
        {approvalStatuses.map((status) => (
          <option key={status.id} value={status.code}>{status.name}</option>
        ))}
      </select>
    );
  };

  // [2.1.9] Badges de anomalias (informativas, não bloqueantes). danger → vermelho, warning → amarelo.
  const renderAnomalyBadges = (anomalies?: FinancialAnomaly[]) => {
    if (!anomalies || anomalies.length === 0) return null;
    const anomalyBadgeClass = (severity: FinancialAnomaly["severity"]) =>
      severity === "danger"
        ? "bg-red-500/10 text-red-600"
        : "bg-yellow-500/10 text-yellow-700";
    return (
      <>
        {anomalies.map((anomaly, index) => (
          <span
            key={`${anomaly.code}-${index}`}
            className={`inline-flex items-center px-2 py-0.5 text-xs font-medium ml-1 mt-1 ${anomalyBadgeClass(anomaly.severity)}`}
            style={{ borderRadius: "var(--radius)", verticalAlign: "middle" }}
            title={anomaly.message}
          >
            {anomaly.message}
          </span>
        ))}
      </>
    );
  };

  // Compact read-only status badges shown in the collapsed row (controls live in the expanded panel).
  const statusOk = "inline-flex items-center px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20";
  const statusMuted = "inline-flex items-center px-2 py-0.5 text-xs text-secondary-text border border-border";
  const renderStatusSummary = (entry: WorkFinancialEntry, isExpense: boolean) => (
    <div className="flex flex-col gap-1 items-start">
      {entry.supplierMatchState === "Linked" ? (
        <span className={statusOk} style={{ borderRadius: "var(--radius)" }}>{entry.matchedSupplierName || "Fornecedor"}</span>
      ) : (
        <span className={statusMuted} style={{ borderRadius: "var(--radius)" }}>Sem fornecedor</span>
      )}
      {isExpense && (entry.expenseCategoryId != null ? (
        <span className={statusOk} style={{ borderRadius: "var(--radius)" }}>{entry.expenseCategoryName || "Categoria"}</span>
      ) : (
        <span className={statusMuted} style={{ borderRadius: "var(--radius)" }}>Sem categoria</span>
      ))}
      {isExpense && entry.imputedCostId != null && (
        <span className={statusOk} style={{ borderRadius: "var(--radius)" }}>Imputado · {formatCurrency(entry.imputedAmount ?? 0)}</span>
      )}
    </div>
  );

  // Consistent text-link action style (matches the rest of the app); adds cursor + hover + color semantics.
  const linkAction = "underline cursor-pointer transition-opacity hover:opacity-70 disabled:opacity-50 disabled:cursor-not-allowed";

  const renderTable = (title: string, rows: WorkFinancialEntry[], isExpense: boolean) => (
    <div className="bg-card border border-border overflow-x-auto" style={{ borderRadius: "var(--radius-card)" }}>
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <h3 className="text-foreground" style={{ fontSize: "var(--text-section-title)", fontWeight: "var(--font-weight-semi-bold)" }}>{title}</h3>
        <span className="text-secondary-text text-sm">{rows.length} registo(s)</span>
      </div>
      <table className="w-full" style={{ minWidth: 1020 }}>
        <thead>
          <tr className="border-b border-border bg-muted text-left align-top">
            <th className="px-3 py-3" style={{ width: 44 }} aria-label="Expandir"></th>
            <th className="px-3 py-3" style={{ width: 92 }}>Data</th>
            <th className="px-3 py-3" style={{ width: 116 }}>Nº documento</th>
            <th className="px-3 py-3" style={{ minWidth: 160 }}>Entidade</th>
            <th className="px-3 py-3 text-right" style={{ width: 104 }}>Total</th>
            <th className="px-3 py-3" style={{ width: 168 }}>Situação</th>
            <th className="px-3 py-3" style={{ width: 150 }}>Estado</th>
            <th className="px-3 py-3" style={{ width: 232 }}>Ações</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={8} className="px-4 py-6 text-center text-secondary-text">Sem registos.</td></tr>
          ) : rows.map((entry) => {
            const document = documents.find((item) => item.id === entry.workFinancialDocumentId);
            const isExpanded = expandedEntryId === entry.id;
            return (
              <Fragment key={entry.id}>
                <tr className="border-b border-border last:border-b-0 align-top">
                  <td className="px-3 py-3 align-top">
                    <DetailsToggle
                      open={expandedEntryId === entry.id}
                      onToggle={() => toggleExpanded(entry.id)}
                      showLabel="Ver detalhes"
                      hideLabel="Ocultar detalhes"
                    />
                  </td>
                  <td className="px-3 py-3 break-words">{formatDate(entry.documentDate)}</td>
                  <td className="px-3 py-3 break-words">{entry.documentNumber || "—"}</td>
                  <td className="px-3 py-3 break-words">
                    {entry.entityName || "—"}
                    {entry.qrCodeRead && (
                      <span
                        className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-green-500/10 text-green-700 ml-1 mt-1"
                        style={{ borderRadius: "var(--radius)", verticalAlign: "middle" }}
                        title="Dados fiscais lidos e confirmados pelo QR code da AT"
                      >
                        QR AT
                      </span>
                    )}
                    {entry.isPotentialDuplicate && (
                      <span
                        className="inline-flex items-center px-2 py-0.5 text-xs bg-yellow-500/10 text-yellow-700 ml-1 mt-1"
                        style={{ borderRadius: "var(--radius)", verticalAlign: "middle" }}
                        title={
                          entry.duplicateOfEntryIds && entry.duplicateOfEntryIds.length > 0
                            ? `Coincide com ${entry.duplicateOfEntryIds.length} outro(s) registo(s) na obra`
                            : "Possível registo duplicado nesta obra"
                        }
                      >
                        Possível duplicado
                      </span>
                    )}
                    {renderAnomalyBadges(entry.anomalies)}
                  </td>
                  <td className="px-3 py-3 text-right font-medium whitespace-nowrap">{formatCurrency(entry.totalAmount)}</td>
                  <td className="px-3 py-3">{renderStatusSummary(entry, isExpense)}</td>
                  <td className="px-3 py-3">
                    <div className="flex flex-col gap-2 items-start">
                      <span>{entry.validationStatus === "Validated" ? "Validado" : "Por validar"}</span>
                      {renderApprovalSelect(entry)}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex gap-3 flex-wrap items-center">
                      {document && (
                        <a
                          className={`${linkAction} text-primary whitespace-nowrap`}
                          href={getWorkFinancialDocumentFileUrl(workId, document.id)}
                          target="_blank"
                          rel="noreferrer"
                          title="Abrir o documento original"
                        >
                          Documento
                        </a>
                      )}
                      {entry.validationStatus !== "Validated" ? (
                        <>
                          <button className={`${linkAction} text-primary whitespace-nowrap`} type="button" disabled={busyEntryId === entry.id} onClick={() => openEdit(entry)}>Editar</button>
                          <button className={`${linkAction} text-green-700 whitespace-nowrap`} type="button" disabled={busyEntryId === entry.id} onClick={() => runEntryMutation(entry.id, () => validateWorkFinancialEntry(workId, entry.id))}>Validar</button>
                        </>
                      ) : (
                        <button className={`${linkAction} text-destructive whitespace-nowrap`} type="button" disabled={busyEntryId === entry.id} onClick={() => runEntryMutation(entry.id, () => unvalidateWorkFinancialEntry(workId, entry.id))}>Invalidar</button>
                      )}
                    </div>
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="border-b border-border last:border-b-0">
                    <td className="px-6 py-5 bg-muted" colSpan={8}>
                      <div className="space-y-4" style={{ position: "sticky", left: 0, width: 1000, maxWidth: "100%" }}>
                        {/* DADOS DA FATURA */}
                        <div className="border border-border bg-muted p-4" style={{ borderRadius: "var(--radius)" }}>
                          <div className="text-secondary-text text-xs mb-2" style={{ textTransform: "uppercase" }}>Dados da fatura</div>
                          <div style={{ display: "flex", flexWrap: "wrap", columnGap: 40, rowGap: 12 }}>
                            <div className="flex flex-col gap-1">
                              <span className="text-secondary-text text-sm">Nº documento</span>
                              <span className="text-foreground text-base break-words">{entry.documentNumber || "—"}</span>
                            </div>
                            <div className="flex flex-col gap-1">
                              <span className="text-secondary-text text-sm">Data</span>
                              <span className="text-foreground text-base">{formatDate(entry.documentDate)}</span>
                            </div>
                            <div className="flex flex-col gap-1">
                              <span className="text-secondary-text text-sm">NIF</span>
                              <span className="text-foreground text-base">{entry.entityTaxNumber || "—"}</span>
                            </div>
                            <div className="flex flex-col gap-1">
                              <span className="text-secondary-text text-sm">Base</span>
                              <span className="text-foreground text-base">{formatCurrency(entry.baseAmount)}</span>
                            </div>
                            <div className="flex flex-col gap-1">
                              <span className="text-secondary-text text-sm">IVA</span>
                              <span className="text-foreground text-base">{formatCurrency(entry.vatAmount)}</span>
                            </div>
                            <div className="flex flex-col gap-1">
                              <span className="text-secondary-text text-sm">Total</span>
                              <span className="text-foreground text-base font-medium">{formatCurrency(entry.totalAmount)}</span>
                            </div>
                            {entry.qrCodeRead && (
                              <div className="flex flex-col gap-1">
                                <span className="text-secondary-text text-sm">Verificação AT</span>
                                <span className="text-foreground text-base break-words" title="Código de verificação AT · ATCUD (lidos do QR code)">
                                  {entry.atVerificationCode || "—"}
                                  {entry.atcud ? <span className="text-secondary-text"> · {entry.atcud}</span> : null}
                                </span>
                              </div>
                            )}
                            <div className="flex flex-col gap-1" style={{ maxWidth: 520 }}>
                              <span className="text-secondary-text text-sm">Descrição</span>
                              <span className="text-foreground text-base break-words">{entry.description || "—"}</span>
                            </div>
                          </div>
                        </div>

                        {/* FORNECEDOR · CATEGORIA · IMPUTAÇÃO */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div className="border border-border bg-muted p-4" style={{ borderRadius: "var(--radius)" }}>
                            <div className="text-secondary-text text-xs mb-2" style={{ textTransform: "uppercase" }}>Fornecedor</div>
                            {renderSupplierChip(entry)}
                          </div>
                          {isExpense && (
                            <div className="border border-border bg-muted p-4" style={{ borderRadius: "var(--radius)" }}>
                              <div className="text-secondary-text text-xs mb-2" style={{ textTransform: "uppercase" }}>Categoria</div>
                              {renderCategoryCell(entry)}
                            </div>
                          )}
                          {isExpense && (
                            <div className="border border-border bg-muted p-4" style={{ borderRadius: "var(--radius)" }}>
                              <div className="text-secondary-text text-xs mb-2" style={{ textTransform: "uppercase" }}>Imputação de custo</div>
                              {renderImputeCell(entry)}
                            </div>
                          )}
                        </div>

                        {/* COMPROVATIVOS DE PAGAMENTO */}
                        {/* [2.2.3] Comprovativos de pagamento anexos a este registo financeiro. */}
                        <div className="border border-border bg-muted p-4" style={{ borderRadius: "var(--radius)" }}>
                          <div className="text-secondary-text text-xs mb-2" style={{ textTransform: "uppercase" }}>Comprovativos de pagamento</div>
                          <EntityDocumentsSection
                            ownerType="financial-entry"
                            ownerId={entry.id}
                            section="payment_proof"
                            title="Comprovativos de pagamento"
                            variant="inline"
                            emptyLabel="Sem comprovativos anexados."
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setIsUploadOpen(true)}
          className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer"
          style={{ borderRadius: "var(--radius)" }}
        >
          + Novo documento financeiro
        </button>
      </div>

      {isUploadOpen && (
        <Modal title="Novo documento financeiro" onClose={() => setIsUploadOpen(false)}>
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Natureza do documento *
              <select value={kindHint} onChange={(event) => setKindHint(event.target.value as WorkFinancialDocumentKind | "")} className="px-3 py-2 border border-border rounded bg-background text-foreground">
                <option value="" disabled>— Receita ou Despesa —</option>
                <option value="Revenue">Receita</option>
                <option value="Expense">Despesa</option>
              </select>
            </label>
            <button
              type="button"
              disabled={isUploading || !kindHint}
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderRadius: "var(--radius)" }}
            >
              {isUploading ? "A carregar…" : "Carregar PDF/imagem"}
            </button>
            <span className="text-xs text-secondary-text">
              Pode selecionar vários ficheiros de uma vez — a natureza escolhida aplica-se a todos.
            </span>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="application/pdf,image/png,image/jpeg"
              disabled={isUploading || !kindHint}
              onChange={(event) => {
                handleUpload(event.target.files);
                event.target.value = "";
              }}
              style={{ display: "none" }}
            />
          </div>
        </Modal>
      )}

      {error && <div className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3" style={{ borderRadius: "var(--radius)" }}>{error}</div>}

      {batchSummary && (
        <div
          className="border border-border bg-muted text-foreground px-4 py-3 text-sm"
          style={{ borderRadius: "var(--radius)" }}
        >
          {batchSummary}
        </div>
      )}

      <div className="bg-card border border-border overflow-x-auto" style={{ borderRadius: "var(--radius-card)" }}>
        <table className="min-w-[900px] w-full">
          <thead><tr className="border-b border-border bg-muted text-left"><th className="px-4 py-3">Documento</th><th className="px-4 py-3">Tipo</th><th className="px-4 py-3">Estado</th><th className="px-4 py-3">Erro</th><th className="px-4 py-3">Ações</th></tr></thead>
          <tbody>
            {isLoading ? <tr><td colSpan={5} className="px-4 py-6 text-center text-secondary-text">A carregar…</td></tr> : documents.length === 0 ? <tr><td colSpan={5} className="px-4 py-6 text-center text-secondary-text">Sem documentos financeiros.</td></tr> : documents.map((document) => (
              <tr key={document.id} className="border-b border-border last:border-b-0">
                <td className="px-4 py-3">{document.fileName}</td>
                <td className="px-4 py-3">{document.entry?.entryType === "Revenue" ? "Receita" : document.entry?.entryType === "Expense" ? "Despesa" : document.documentKindHint}</td>
                <td className="px-4 py-3"><span className={`inline-flex px-2 py-1 border text-xs ${statusClass(document)}`}>{statusLabel(document)}</span></td>
                <td className="px-4 py-3 text-destructive max-w-[280px] truncate">{document.processingError || "—"}</td>
                <td className="px-4 py-3"><div className="flex gap-3 flex-wrap">
                  <a className={`${linkAction} text-primary`} href={getWorkFinancialDocumentFileUrl(workId, document.id)} target="_blank" rel="noreferrer">Abrir</a>
                  {document.entry?.validationStatus !== "Validated" && (
                    <>
                      <button className={`${linkAction} text-primary`} type="button" onClick={() => reprocessWorkFinancialDocument(workId, document.id).then(replaceDocument)}>Reprocessar</button>
                      <button className={`${linkAction} text-destructive`} type="button" disabled={deletingId === document.id} onClick={() => handleDelete(document.id)}>{deletingId === document.id ? "A eliminar…" : "Eliminar"}</button>
                    </>
                  )}
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {renderTable("Receitas", revenues, false)}
      {renderTable("Despesas", expenses, true)}

      {editingEntry && draft && (
        <Modal
          title="Editar registo financeiro"
          onClose={() => { setEditingEntry(null); setDraft(null); }}
          footer={
            <>
              <button type="button" onClick={() => { setEditingEntry(null); setDraft(null); }} className="px-4 py-2 border border-border">Cancelar</button>
              <button type="button" onClick={saveEdit} className="px-4 py-2 bg-primary text-primary-foreground">Guardar</button>
            </>
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Tipo
                <select value={draft.entryType} onChange={(e) => setDraft({ ...draft, entryType: e.target.value as WorkFinancialEntryType })} className="px-3 py-2 border border-border rounded bg-background text-foreground"><option value="Revenue">Receita</option><option value="Expense">Despesa</option></select>
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Nº documento
                <input placeholder="Nº documento" value={draft.documentNumber ?? ""} onChange={(e) => setDraft({ ...draft, documentNumber: e.target.value })} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Data do documento
                <input type="date" value={draft.documentDate ?? ""} onChange={(e) => setDraft({ ...draft, documentDate: e.target.value || null })} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Entidade
                <input placeholder="Entidade" value={draft.entityName ?? ""} onChange={(e) => setDraft({ ...draft, entityName: e.target.value })} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                NIF
                <input placeholder="NIF" value={draft.entityTaxNumber ?? ""} onChange={(e) => setDraft({ ...draft, entityTaxNumber: e.target.value })} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
                Descrição
                <input placeholder="Descrição" value={draft.description ?? ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Base (€)
                <input placeholder="Base" value={draft.baseAmount ?? ""} onChange={(e) => setDraft({ ...draft, baseAmount: parseNumber(e.target.value) })} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                IVA (€)
                <input placeholder="IVA" value={draft.vatAmount ?? ""} onChange={(e) => setDraft({ ...draft, vatAmount: parseNumber(e.target.value) })} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
                Total (€)
                <input placeholder="Total" value={draft.totalAmount ?? ""} onChange={(e) => setDraft({ ...draft, totalAmount: parseNumber(e.target.value) })} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
              </label>
            </div>
        </Modal>
      )}
    </div>
  );
}
